import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { AccountVariable, AccountVariableType } from '../../account-settings/account-settings-models';
import ColorHelpers from '../helpers/color-helpers';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from 'app/core/snack-bar.service';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { AccountSettingsService } from 'app/account-settings/account-settings.service';
import { BrandVariablesService } from 'app/account/brand-variables.service';
import { IdentityService } from 'app/core/identity/identity.service';
import { SiteRoleService } from 'app/core/identity/site-role.service';
import { BrandVariable } from 'app/account/brand-variable';

export class RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
  constructor(r: number, g: number, b: number, a: number) {
    this.r = r;
    this.g = g;
    this.b = b;
    this.a = a;
  }
}
export class RgbColor {
  r: number;
  g: number;
  b: number;
  constructor(r: number, g: number, b: number) {
    this.r = r;
    this.g = g;
    this.b = b;
  }
}
export class ColorPalette {
  colors: Array<string>;
  constructor(colors: Array<string>) {
    this.colors = colors;
  }
}

@Component({
  selector: 'app-color-picker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ColorPickerComponent implements OnInit, OnChanges {
  @Input() set inputAccountColors(colors: Array<AccountVariable>) {
    if (colors?.length) {
      this.accountColors = colors;
    }
  }
  @Input() sourceItem: string;
  @Input() set firstTab(_firstTab: number) {
    this.viewColorPicker = 'firstTab';
  }
  @Input() set currentColor(colorString: string) {
    if (colorString) {
      this.parseInputColor(colorString);
      this.setColorInputLabel();
    }
  }
  @Input() set isBrandVariableSetOutside(val) {
    if (val) {
      this.isBrandVariableColorSet = val;
      this.setColorInputLabel();
    }
  }
  @Input() brandSettingColor?: string;
  @Input() brandSettingColorLabel?: string;

  @Input() isSourceGradient = false;
  @Input() isGradientNeeded: boolean = false;
  @Input() sessionKey = 0;

  @Input() paletteClosed: boolean;

  @Output() customColorUpdated: EventEmitter<string | null> = new EventEmitter();
  @Output() enterInput: EventEmitter<string> = new EventEmitter();
  @Output() gradientColorUpdated: EventEmitter<string> = new EventEmitter();

  public colorInputValue: string;
  public colorInputError: string;
  public colorInputLabel: string;

  public hue: string;
  public rgbColor: RgbColor = { r: 0, g: 0, b: 0 };
  public opacity: number;
  public lastEmitedColor = undefined;
  public isAccountVariable = false;
  public currentAccountVariable: string;
  public viewColorPicker = 'firstTab';

  public showGradientTool = false;
  public transparentColor = '#00000000';
  public white = '#ffffffff';
  public palettes: string[] = [
    '#ccccccff',
    '#999999ff',
    '#666666ff',
    '#333333ff',
    '#000000ff',
    '#ff3131ff',
    '#ff5757ff',
    '#ff66c4ff',
    '#cd6ce6ff',
    '#8c52ffff',
    '#5e17ebff',
    '#00bf63ff',
    '#7ed957ff',
    '#c1ff72ff',
    '#ffde59ff',
    '#ffbd59ff',
    '#ff914dff',
    '#0097b2ff',
    '#0cc0dfff',
    '#5ce1e6ff',
    '#38b6ffff',
    '#5271ffff',
    '#004aadff',
    '#aabbd7ff',
    '#7f9eb1ff',
    '#4f6d7aff',
    '#a0a0a0ff',
    '#585858ff',
    '#2e2e2eff',
    '#add8e6ff',
    '#a4d5d5ff',
    '#66ccccff',
    '#3399ccff',
    '#006699ff',
    '#003366ff',
    '#b3d9b3ff',
    '#ccffccff',
    '#b3e6e6ff',
    '#99c2e6ff',
    '#66aaccff',
    '#336699ff',
    '#e0d99dff',
    '#c2c67aff',
    '#a8b85fff',
    '#8b9a56ff',
    '#6f7b46ff',
    '#4b5d32ff',
    '#ffcc00ff',
    '#ffa500ff',
    '#ff7f50ff',
    '#d45b47ff',
    '#b33c3cff',
    '#800000ff',
    '#e6d1b3ff',
    '#d8c1a0ff',
    '#bfa58dff',
    '#a97c5bff',
    '#7a4b3bff',
    '#4e3629ff',
    '#ffd27fff',
    '#ffeb99ff',
    '#ffffb3ff',
    '#d9f2a5ff',
    '#b3e5a5ff',
    '#8dc69eff',
    '#f6c6e3ff',
    '#fce1f1ff',
    '#fad0dcff',
    '#f4c6a1ff',
    '#ffa07aff',
    '#ff6666ff',
    '#aabbd7ff',
    '#b7aeddff',
    '#a98bcfff',
    '#9e69c3ff',
    '#8a47b8ff',
    '#6a1b9aff',
    '#666666ff',
    '#333333ff',
    '#000000ff',
  ];
  public brandColors: Array<{ label: string; color: string }> = [];
  public accountColors: Array<AccountVariable> = [];
  public usedColors: string[] = [];
  public isBrandVariableColorSet: boolean;
  public typeColor: TypeColor;
  public TypeColor = TypeColor;
  public showEditLink: boolean;
  private readonly siteRoleService = inject(SiteRoleService);

  constructor(
    private translateService: TranslateService,
    private dialog: MatDialog,
    private readonly snackbarService: SnackBarService,
    private readonly widgetEditorDataService: WidgetEditorDataService,
    private accountSettingService: AccountSettingsService,
    private brandVariablesService: BrandVariablesService,
    public readonly identityService: IdentityService,
  ) {}

  ngOnInit() {
    this.getBrandVariables();
    this.getUsedColors();
    this.setEditLinkVisibility();

    this.brandSettingColor = this.brandSettingColor?.toLowerCase();
    this.accountColors = this.accountColors.map((color) => ({
      ...color,
      value: color.value?.toLowerCase(),
    }));

    setTimeout(() => {
      this.setColorInputLabel();
    }, 10);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paletteClosed'] && changes['paletteClosed'].currentValue === true) {
      this.createUsedColor();
    }
    if (changes['brandSettingColorLabel']) {
      this.removeDuplicate();
    }
  }

  setColorInputLabel() {
    if (this.isBrandVariableColorSet && this.colorInputValue === this.brandSettingColor && !this.isAccountVariable) {
      this.colorInputLabel = this.brandSettingColorLabel;
      this.typeColor = TypeColor.BrandVarColor;
    } else if (this.colorInputLabel !== 'palette') {
      const firstMatch = this.brandColors.find((brandColor) => brandColor.color === this.colorInputValue);
      if (firstMatch && !this.isAccountVariable) {
        this.colorInputLabel = firstMatch.label;
        this.typeColor = TypeColor.BrandColor;
      } else if (this.isAccountVariable) {
        this.typeColor = TypeColor.AccountColor;
      } else if (
        this.usedColors.find((usedColor) => usedColor === this.colorInputValue) ||
        this.colorInputValue === this.transparentColor
      ) {
        this.typeColor = TypeColor.UsedColor;
      } else {
        this.typeColor = TypeColor.PaletteColor;
      }
    }
  }

  setEditLinkVisibility() {
    if (this.widgetEditorDataService.widget && this.siteRoleService.isAccountAdmin()) {
      this.showEditLink = true;
    } else {
      this.showEditLink = false;
    }
  }

  public getBrandVariables(): void {
    if (this.widgetEditorDataService.widget) {
      const brandVariablesFromWidget = this.widgetEditorDataService.widget.brandVariables;
      if (brandVariablesFromWidget) {
        this.setBrandColors(brandVariablesFromWidget);
      }
    } else {
      this.brandVariablesService.getLatest().subscribe((latestBrandVariables) => {
        if (latestBrandVariables) {
          this.setBrandColors(latestBrandVariables);
        }
      });
    }
  }

  private setBrandColors(brandVariables: BrandVariable): void {
    const brandColors = Object.entries({
      canvas: brandVariables.canvas.backgroundColor,
      closeButton: brandVariables.canvas.closeButtonColor,
      headline: brandVariables.headline.color,
      text: brandVariables.text.color,
      buttonBackground: brandVariables.button.backgroundColor,
      buttonText: brandVariables.button.color,
      buttonBorder: brandVariables.button.border?.color,
      inputLabel: brandVariables.label.color,
      inputField: brandVariables.input.backgroundColor,
      inputText: brandVariables.input.color,
      inputBorder: brandVariables.input.border.color,
    }).map(([label, color]) => ({ label, color }));
    this.brandColors = brandColors
      .filter((brandColor) => brandColor.color != null && brandColor.color.length > 7)
      .map((brandColor) => ({
        ...brandColor,
        color: brandColor.color?.toLowerCase(),
      }));
    this.removeDuplicate();
  }

  public removeDuplicate() {
    if (this.brandSettingColorLabel) {
      const removedBrandSettingColor = this.brandColors.filter((color) => {
        return color.label.toString() !== this.brandSettingColorLabel;
      });
      this.brandColors = removedBrandSettingColor;
    }
  }

  public formatBrandColorLabel(label: string) {
    if (!label) return '';
    const withSpaces = label.replace(/([a-z])([A-Z])/g, '$1 $2');
    const lowercased = withSpaces.toLowerCase();
    return lowercased[0].toUpperCase() + lowercased.slice(1);
  }

  public getUsedColors() {
    const usedColors = localStorage.getItem('usedColors');
    if (usedColors) {
      this.usedColors = JSON.parse(usedColors);
    }
  }

  public createUsedColor() {
    if (!this.validateInputColor()) return;
    this.getUsedColors();
    const usedColor = this.colorInputValue;
    const existsInAccountColors = this.accountColors?.some(
      (accountColor) => accountColor.value === this.colorInputValue,
    );
    const existsInBrandColors = this.brandColors?.some((brandColor) => brandColor.color === this.colorInputValue);
    const existsInUsedColors = this.usedColors?.some((usedColor) => usedColor === this.colorInputValue);
    const isTransparent = this.colorInputValue.slice(-2) === '00';
    if (!existsInAccountColors && !existsInBrandColors && !isTransparent && !existsInUsedColors) {
      this.usedColors.push(usedColor);
      if (this.usedColors.length > 11) {
        this.usedColors.shift();
      }
      localStorage.setItem('usedColors', JSON.stringify(this.usedColors));
    }
  }

  private parseInputColor(colorString: string) {
    if (!colorString) {
      throw new Error('Color must be present');
    }
    if (ColorHelpers.isColorGradient(colorString)) {
      this.colorInputValue = colorString;
      return;
    }
    let colorToParse = colorString;
    let isAccountVariable = false;
    let currentAccountVariable = undefined;
    if (ColorHelpers.isAccountVariable(colorString)) {
      this.colorInputLabel = colorString.replace(/[{}]/g, '');
      colorToParse = ColorHelpers.getColorFromAccountVariable(colorString, this.accountColors);
      if (!colorToParse) {
        console.warn('There was no variable with key, probably removed: ' + colorString);
        // Add transparent color if the property was removed
        colorToParse = '#00000000';
      }
      isAccountVariable = true;
      currentAccountVariable = colorString.substring(1, colorString.length - 1);

      this.isAccountVariable = isAccountVariable;
      this.currentAccountVariable = currentAccountVariable;
    } else {
      this.isAccountVariable = false;
    }

    const isHexColor = ColorHelpers.validateHexColor(colorToParse);
    if (isHexColor) {
      const rgbaColor = ColorHelpers.convertHexToDecimals(colorToParse) as RgbaColor;
      this.rgbColor = { r: rgbaColor.r, g: rgbaColor.g, b: rgbaColor.b };
      this.opacity = rgbaColor.a;
      this.updateColorInputFromParts();
      return;
    }

    const isRgbOrRgbaColor = ColorHelpers.validateRgbaColor(colorToParse);
    if (isRgbOrRgbaColor) {
      const rgbaColor = ColorHelpers.rgbaStringToNumbers(colorToParse);
      this.rgbColor = { r: rgbaColor.r, g: rgbaColor.g, b: rgbaColor.b };
      this.opacity = rgbaColor.a;
      this.updateColorInputFromParts();
      return;
    }
  }

  private updateColorInputFromParts() {
    if (this.rgbColor && this.opacity !== undefined) {
      this.colorInputValue = ColorHelpers.rgbaNumbersToHex(
        this.rgbColor.r,
        this.rgbColor.g,
        this.rgbColor.b,
        this.opacity,
      );
    }
  }

  public setNewColorFromArea(rgbColor: RgbColor) {
    this.rgbColor = new RgbColor(rgbColor.r, rgbColor.g, rgbColor.b);
    const rgbaColor = new RgbaColor(rgbColor.r, rgbColor.g, rgbColor.b, this.opacity);
    this.colorInputValue = ColorHelpers.rgbaColorToHex(rgbaColor);
    this.colorInputError = undefined;
    this.emitCustomColorUpdate(ColorHelpers.rgbaColorToHex(rgbaColor));
  }

  public setNewBrandSettingColor(color: string, label: string, typeColor) {
    const rgbaColor = ColorHelpers.convertHexToDecimals(color) as RgbaColor;
    this.rgbColor = rgbaColor;
    this.colorInputValue = color;
    this.colorInputError = undefined;
    if (typeColor === TypeColor.BrandVarColor) {
      this.isBrandVariableColorSet = true;
      this.emitCustomColorUpdate(null);
    } else {
      this.isBrandVariableColorSet = false;
      this.emitCustomColorUpdate(color);
    }
    this.colorInputLabel = label;
    this.typeColor = typeColor;
  }

  public setNewHueFromSlider(rgbColor: RgbColor) {
    this.rgbColor = new RgbColor(rgbColor.r, rgbColor.g, rgbColor.b);
    this.colorInputValue = ColorHelpers.rgbaColorToHex(new RgbaColor(rgbColor.r, rgbColor.g, rgbColor.b, this.opacity));
    this.updateColorInputFromParts();
    this.emitCustomColorUpdate(
      ColorHelpers.rgbaNumbersToHex(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b, this.opacity),
    );
  }

  public setNewOpacityFromSlider(opacity: number) {
    this.opacity = opacity;
    this.updateColorInputFromParts();
    this.emitCustomColorUpdate(
      ColorHelpers.rgbaNumbersToHex(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b, this.opacity),
    );
  }

  private emitCustomColorUpdate(hexColor: string | null) {
    this.lastEmitedColor = hexColor;
    this.isAccountVariable = false;
    this.currentAccountVariable = undefined;
    this.customColorUpdated.emit(hexColor);
  }

  public setAccountColorAsChosen(accountColor: AccountVariable, typeColor) {
    this.isBrandVariableColorSet = false;
    const accountColorKey = `{${accountColor.key}}`;
    this.parseInputColor(accountColorKey);
    this.lastEmitedColor = accountColorKey;
    this.colorInputError = undefined;
    this.colorInputLabel = accountColor.key;
    this.customColorUpdated.emit(accountColorKey);
    if (accountColor.value.length === 7) {
      setTimeout(() => {
        this.colorInputValue = accountColor.value;
      }, 10);
    }
    this.typeColor = typeColor;
  }

  public setColorFromPalette(colorHexCode: string, typeColor) {
    this.isBrandVariableColorSet = false;
    this.colorInputValue = colorHexCode;
    this.colorInputError = undefined;
    this.colorInputLabel = 'palette';
    this.typeColor = typeColor;
    if (ColorHelpers.isColorGradient(colorHexCode)) {
      this.emitCustomColorUpdate(colorHexCode);
      return;
    }
    this.parseInputColor(colorHexCode);
    const rgbaColor = new RgbaColor(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b, this.opacity);
    this.emitCustomColorUpdate(ColorHelpers.rgbaColorToHex(rgbaColor) ?? null);
  }

  public colorInputChanged() {
    if (this.validateInputColor()) {
      this.dialog.closeAll();
      this.parseInputColor(this.colorInputValue);
      const rgbaColor = new RgbaColor(this.rgbColor.r, this.rgbColor.g, this.rgbColor.b, this.opacity);
      this.emitCustomColorUpdate(ColorHelpers.rgbaColorToHex(rgbaColor));
      this.enterInput.emit();
    }
  }

  public validateInputColor(): boolean {
    this.colorInputValue = this.colorInputValue.trim();
    this.colorInputError = undefined;
    if (!this.colorInputValue) {
      this.colorInputError = this.translateService.instant('generic.validation.error-required');
    } else {
      const isHexColor = ColorHelpers.validateHexColor(this.colorInputValue);
      let isRgbaColor = false;
      if (!isHexColor) {
        isRgbaColor = ColorHelpers.validateRgbaColor(this.colorInputValue);
      }

      if (!isHexColor && !isRgbaColor) {
        this.colorInputError = this.translateService.instant('generic.validation.error-invalid-color-code');
      }
    }
    return !this.colorInputError;
  }

  public createNewAccountColor() {
    if (!this.validateInputColor()) return;
    const accountColor = {
      key: ColorHelpers.getNextDefaultColorName(this.accountColors),
      type: AccountVariableType.Color,
      value: this.colorInputValue,
    } as AccountVariable;

    const existsInAccountColors = this.accountColors?.some(
      (accountColor) => accountColor.value === this.colorInputValue,
    );
    const existsInBrandColors = this.brandColors?.some((brandColor) => brandColor.color === this.colorInputValue);

    if (this.accountColors.length < 25) {
      if (!existsInAccountColors && !existsInBrandColors) {
        this.accountSettingService.createAccountVariable(accountColor).subscribe((result) => {
          this.widgetEditorDataService.loadAccountVariables().subscribe((res) => {
            if (res) {
              this.accountColors.push(result);
              this.snackbarService.success('settings.customization.colors.create-success');
            }
          });
        });
      } else {
        this.snackbarService.error('widgets.color-picker.add-color-duplicate');
      }
    } else {
      this.snackbarService.error('widgets.color-picker.add-color-limit');
    }
  }

  public handleTabClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  public toggleGradientTool(): void {
    this.showGradientTool = !this.showGradientTool;
  }

  public onGradientUpdated(gradientCss: string): void {
    this.customColorUpdated.emit(gradientCss);
    this.colorInputValue = gradientCss;
  }

  public onGradientApplied(gradientCss: string): void {
    this.saveGradientToUsedColors(gradientCss);
    this.customColorUpdated.emit(gradientCss);
    this.colorInputValue = gradientCss;
  }

  public onGradientToolClosed(): void {
    this.showGradientTool = false;
    this.enterInput.emit();
  }

  public deleteUsedColor(color: string): void {
    this.usedColors = this.usedColors.filter((c) => c !== color);
    localStorage.setItem('usedColors', JSON.stringify(this.usedColors));
  }

  private saveGradientToUsedColors(gradientCss: string): void {
    this.getUsedColors();
    const existsInUsedColors = this.usedColors.some((c) => c === gradientCss);
    if (!existsInUsedColors) {
      this.usedColors.push(gradientCss);
      if (this.usedColors.length > 11) {
        this.usedColors.shift();
      }
      localStorage.setItem('usedColors', JSON.stringify(this.usedColors));
    }
  }
}

export enum TypeColor {
  PaletteColor = 'paletteColor',
  UsedColor = 'usedColor',
  AccountColor = 'accountColor',
  BrandColor = 'brandColor',
  BrandVarColor = 'brandVarColor',
}
